import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Descriptions, Drawer, Modal, Space, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import * as groupingAgentApi from '@apis/grouping-agent';
import * as groupMonitoringApi from '@apis/group-monitoring';
import { EnrollmentSourceDetail } from '@models/grouping-agent';
import { ProductOrderViewModel } from '@models/productOrderView';

const { Text, Paragraph } = Typography;

const diagnosisColor: Record<string, string> = {
  ok: 'green',
  missing: 'default',
  sync_gap: 'orange',
  parse_suspected: 'volcano',
  notes_parsed: 'cyan',
};

const diagnosisLabel: Record<string, string> = {
  ok: '정상',
  missing: '이름 없음',
  sync_gap: 'sync 불일치',
  parse_suspected: '파싱 의심',
  notes_parsed: 'notes 파싱',
};

const OrderPreviewModal: React.FC<{
  open: boolean;
  onClose: () => void;
  productOrder: ProductOrderViewModel | null;
}> = ({ open, onClose, productOrder }) => {
  if (!productOrder) return null;
  const payment = productOrder.payment;
  const buyer = productOrder.buyer;

  return (
    <Modal title="주문 원본" open={open} onCancel={onClose} footer={null} width={800}>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="주문 ID">{productOrder._id}</Descriptions.Item>
        <Descriptions.Item label="주문번호">{productOrder.uuid || productOrder.uid || '-'}</Descriptions.Item>
        {productOrder.createdAt && (
          <Descriptions.Item label="주문일시">
            {dayjs(productOrder.createdAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
        )}
        {payment && (
          <Descriptions.Item label="결제">
            {payment.amount?.toLocaleString?.() ?? payment.amount}원 / {payment.method}
          </Descriptions.Item>
        )}
        {buyer && (
          <Descriptions.Item label="구매자">
            {buyer.name} / {buyer.phone}
          </Descriptions.Item>
        )}
        {productOrder.memo && (
          <Descriptions.Item label="memo">
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {JSON.stringify(productOrder.memo, null, 2)}
            </pre>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Modal>
  );
};

const SourceBody: React.FC<{ detail: EnrollmentSourceDetail }> = ({ detail }) => {
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const viewInfo = (detail.view.scheduleInfo as Record<string, unknown>) || {};
  const sourceInfo = detail.sourceSchedule?.scheduleInfo || {};

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        {detail.namePending && <Tag color="orange">이름 보충 필요</Tag>}
        <Tag color={diagnosisColor[detail.nameDiagnosis.status] || 'default'}>
          {diagnosisLabel[detail.nameDiagnosis.status] || detail.nameDiagnosis.status}
        </Tag>
        {detail.agentDisplayName && <Tag>에이전트 표시: {detail.agentDisplayName}</Tag>}
      </Space>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {detail.nameDiagnosis.hint}
      </Paragraph>
      {detail.nameDiagnosis.parsedFromNotes && (
        <Descriptions
          title="notes 파싱 결과"
          bordered
          size="small"
          column={1}
          items={[
            { key: 'p-name', label: '이름', children: detail.nameDiagnosis.parsedFromNotes.name || '-' },
            {
              key: 'p-birth',
              label: '출생연도',
              children: detail.nameDiagnosis.parsedFromNotes.birthYear || '-',
            },
            {
              key: 'p-friends',
              label: '친구',
              children: detail.nameDiagnosis.parsedFromNotes.friends || '-',
            },
          ]}
        />
      )}
      {detail.nameDiagnosis.memoSnippet && (
        <Paragraph>
          <Text>
            <Text strong>memo/비고 발췌: </Text>
            {detail.nameDiagnosis.memoSnippet}
          </Text>
        </Paragraph>
      )}
      {detail.nameDiagnosis.productQuestionTypes && detail.nameDiagnosis.productQuestionTypes.length > 0 && (
        <Text type="secondary">상품 questionTypes: {detail.nameDiagnosis.productQuestionTypes.join(', ')}</Text>
      )}

      <Descriptions
        title="program_schedules_view"
        bordered
        size="small"
        column={1}
        items={[
          { key: 'view-name', label: '참가자명', children: String(viewInfo.name || '-') },
          {
            key: 'view-option',
            label: '옵션/회차',
            children: String(detail.view.optionName || detail.view.scheduleName || '-'),
          },
          {
            key: 'view-buyer',
            label: '구매자',
            children: `${String(detail.view.buyerName || '-')} / ${String(detail.view.buyerPhone || '-')}`,
          },
          {
            key: 'view-channel',
            label: '채널',
            children: String(detail.view.sourceName || detail.view.source || '-'),
          },
          ...(detail.view.notes ? [{ key: 'view-notes', label: 'notes', children: String(detail.view.notes) }] : []),
        ]}
      />

      <Descriptions
        title="원본 주문 스케줄"
        bordered
        size="small"
        column={1}
        items={[
          { key: 'src-name', label: '참가자명', children: String(sourceInfo.name || '-') },
          { key: 'src-option', label: '옵션', children: detail.sourceSchedule?.optionName || '-' },
          { key: 'src-schedule', label: '회차명', children: detail.sourceSchedule?.scheduleName || '-' },
          ...(detail.sourceSchedule?.notes
            ? [{ key: 'src-notes', label: 'notes', children: detail.sourceSchedule.notes }]
            : []),
        ]}
      />

      {detail.order && (
        <Descriptions
          title="주문 요약"
          bordered
          size="small"
          column={1}
          items={[
            { key: 'order-type', label: '유형', children: detail.order.sourceType },
            {
              key: 'order-uid',
              label: '주문번호',
              children: detail.order.orderUid || detail.order.orderId,
            },
            { key: 'order-channel', label: '채널', children: detail.order.channel || '-' },
            ...(detail.order.purchases?.map((p, i) => ({
              key: `purchase-${i}`,
              label: `구매 ${i + 1}`,
              children: `${p.name} x${p.count ?? 1}`,
            })) || []),
          ]}
        />
      )}

      {detail.orderForModal && (
        <Button type="primary" onClick={() => setOrderModalOpen(true)}>
          주문 원본 모달
        </Button>
      )}

      <OrderPreviewModal
        open={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        productOrder={detail.orderForModal?.data as ProductOrderViewModel}
      />
    </Space>
  );
};

interface Props {
  scheduleId: string | null;
  open: boolean;
  onClose: () => void;
  apiSource?: 'grouping-agent' | 'group-monitoring';
}

const EnrollmentSourceDrawer: React.FC<Props> = ({ scheduleId, open, onClose, apiSource = 'grouping-agent' }) => {
  const query = useQuery({
    queryKey: ['enrollment-source', apiSource, scheduleId],
    queryFn: () =>
      apiSource === 'group-monitoring'
        ? groupMonitoringApi.getGroupMonitoringEnrollmentSource(scheduleId!)
        : groupingAgentApi.getGroupingAgentEnrollmentSource(scheduleId!),
    enabled: open && !!scheduleId,
  });

  const detail = query.data?.data;

  return (
    <Drawer title="예약 원본 비교" open={open} onClose={onClose} width={560} destroyOnClose>
      {query.isLoading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      )}
      {detail && !query.isLoading && <SourceBody detail={detail} />}
    </Drawer>
  );
};

export default EnrollmentSourceDrawer;
